/* eslint-disable react/button-has-type */
import {
  ReactNode,
  AnchorHTMLAttributes,
  ComponentProps,
  ButtonHTMLAttributes,
} from 'react';

import clsx from 'clsx';
import {
  Link as ReactRouterLink,
  NavLink as ReactRouterNavLink,
} from 'react-router-dom';

import Link from '../Link';

import classes from './Button.module.css';

export type TButtonVariant =
  | 'PRIMARY'
  | 'SECONDARY'
  | 'ACTION'
  | 'ACTION_SECONDARY'
  | 'OUTLINE'
  | 'OUTLINE_SECONDARY'
  | 'REMOVE'
  | 'UNSTYLED';

export type TButtonSize = 'xs' | 'sm' | 'md';

export type TChildrenProps = {
  defaultTextClass: string;
};

interface IPropsBase {
  'data-testid'?: string;
  children?: ReactNode | ((props: TChildrenProps) => ReactNode);
  className?: string;
  endIcon?: ReactNode;
  isFullWidth?: boolean;
  label?: string;
  overrideClasses?: {
    root?: string;
    text?: string;
    startIcon?: string;
    endIcon?: string;
  };
  size?: TButtonSize;
  startIcon?: ReactNode;
  textAlign?: 'left' | 'right' | 'center';
  variant?: TButtonVariant;
}

export type TButtonAsLink = IPropsBase & {
  disabled?: never;
  type?: never;
} & (
    | ({ as: 'a' } & AnchorHTMLAttributes<HTMLAnchorElement>)
    | ({ as: typeof Link } & ComponentProps<typeof Link>)
    | ({ as: typeof ReactRouterLink } & ComponentProps<typeof ReactRouterLink>)
    | ({ as: typeof ReactRouterNavLink } & ComponentProps<
        typeof ReactRouterNavLink
      >)
  );

export type TButtonAsButton = IPropsBase &
  ButtonHTMLAttributes<HTMLButtonElement> & { as?: never };

export type TButton = TButtonAsButton | TButtonAsLink;

export default function Button(props: TButton) {
  const {
    'data-testid': dataTestId,
    children,
    className,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    disabled = false,
    endIcon = null,
    isFullWidth = false,
    label = '',
    overrideClasses = {},
    size,
    startIcon = null,
    textAlign = 'center',
    type = 'button',
    variant = 'PRIMARY',
    ...otherProps
  } = props;
  const { root = className } = overrideClasses;

  function getContent() {
    if (children) {
      return typeof children === 'function'
        ? children({ defaultTextClass: classes.button_content })
        : children;
    }
    return (
      <div
        className={clsx(overrideClasses.text, classes.button_content)}
        data-testid="button_content"
      >
        {startIcon && (
          <span className={clsx(overrideClasses.startIcon, classes.start_icon)}>
            {startIcon}
          </span>
        )}
        {label}
        {endIcon && (
          <span className={clsx(overrideClasses.endIcon, classes.end_icon)}>
            {endIcon}
          </span>
        )}
      </div>
    );
  }

  const sharedProps = {
    className: clsx(root, classes.button, {
      [classes.primary]: variant === 'PRIMARY',
      [classes.secondary]: variant === 'SECONDARY',
      [classes.outline]: variant === 'OUTLINE',
      [classes.remove]: variant === 'REMOVE',
      [classes.outlineSecondary]: variant === 'OUTLINE_SECONDARY',
      [classes.action]: variant === 'ACTION',
      [classes.action_secondary]: variant === 'ACTION_SECONDARY',
      [classes.unstyled]: variant === 'UNSTYLED',
      [classes.full_width]: isFullWidth,
      [classes.xs]: size === 'xs',
      [classes.sm]: size === 'sm',
      [classes.md]: size === 'md',
      [classes.text_left]: textAlign === 'left',
      [classes.text_right]: textAlign === 'right',
      [classes.icon_button]: !!(startIcon || endIcon),
    }),
    'data-testid': dataTestId,
  };

  if (!props.as) {
    return (
      <button
        type={type}
        disabled={disabled}
        onClick={props.onClick}
        {...(otherProps as TButtonAsButton)}
        {...sharedProps}
      >
        {getContent()}
      </button>
    );
  }

  if (props.as === 'a') {
    return (
      <a
        {...sharedProps}
        href={props.href}
        onClick={props.onClick}
        {...(otherProps as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {getContent()}
      </a>
    );
  }

  if (props.as === Link) {
    return (
      <Link
        {...sharedProps}
        {...(otherProps as ComponentProps<typeof Link>)}
        to={props.to}
        onClick={props.onClick}
      >
        {getContent()}
      </Link>
    );
  }

  if (props.as === ReactRouterNavLink) {
    return (
      <ReactRouterNavLink
        {...sharedProps}
        {...(otherProps as ComponentProps<typeof ReactRouterNavLink>)}
        to={props.to}
        onClick={props.onClick}
      >
        {getContent()}
      </ReactRouterNavLink>
    );
  }

  if (props.as === ReactRouterLink) {
    return (
      <ReactRouterLink
        {...sharedProps}
        {...(otherProps as ComponentProps<typeof ReactRouterLink>)}
        to={props.to}
        onClick={props.onClick}
      >
        {getContent()}
      </ReactRouterLink>
    );
  }

  // This case won't be hit, adding to appease TS.
  return null;
}
